import { Alert, Box, Text, Select } from '@nimbus-ds/components';
import { CheckCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { CreditCardFormFields as CreditCardFormFieldsType } from 'domains/Billing/Checkout/hooks/utils';
import {
  getIdentificationNumberPlaceholder,
  handleIdentificationNumber,
  handleCardNumber,
  handleIndentificationType,
  handleExpirationDate,
} from 'domains/Billing/Checkout/pages/PayWithCreditCard/components/CreditCardDataCard/utils';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';

interface CreditCardFormFieldsProps {
  formValues: CreditCardFormFieldsType;
  setFieldValue: (field: string, value: string) => void;
  errors: any;
  country: string;
  recurrentPaymentCompatible: boolean;
}

function Fields({
  formValues,
  setFieldValue,
  errors,
  country,
  recurrentPaymentCompatible = false,
}: CreditCardFormFieldsProps) {
  const t = useTranslationBilling(
    'checkout.payWithCreditCard.creditCardDataCard',
  );

  const getInputAttributes = (
    field: string,
    placeholder?: string,
    type = 'input',
  ) => {
    const error = errors[field];
    const helpText = error && error !== '' ? t(error) : '';
    const appearance = helpText !== '' ? ('danger' as const) : undefined;

    return {
      label: t(`form.${field}.label`),
      id: `${type}_${field}`,
      name: field,
      showHelpText: !!helpText,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
        setFieldValue(field, event.target.value),
      value: formValues[field],
      helpText,
      appearance,
      placeholder,
      helpIcon: helpText ? CheckCircleIcon : undefined,
    };
  };

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <FormField.Input
        {...getInputAttributes('cardNumber', '1234 5678 9012 3456')}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleCardNumber(event.target.value, setFieldValue)
        }
      />
      <FormField.Input {...getInputAttributes('cardholderName')} />
      <Box display="flex" flexDirection="row" gap="4">
        <Box display="flex" flexDirection="column" flexGrow="1">
          <FormField.Input
            {...getInputAttributes('expirationDate', 'mm/aa')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleExpirationDate(event.target.value, setFieldValue)
            }
          />
        </Box>
        <Box display="flex" flexDirection="column" flexGrow="1">
          <FormField.Input {...getInputAttributes('securityCode', '123')} />
        </Box>
      </Box>
      {country !== 'MX' && (
        <Box display="flex" flexDirection="row" gap="4">
          <Box display="flex" flexDirection="column" flex="1 1 35%">
            <FormField.Select
              {...getInputAttributes('identificationType', '', 'select')}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                handleIndentificationType(event.target.value, setFieldValue)
              }
            >
              <Select.Option value="DNI" label="DNI" />
            </FormField.Select>
          </Box>
          <Box display="flex" flexDirection="column" flex="1 1 65%">
            <FormField.Input
              {...getInputAttributes(
                'identificationNumberParsed',
                getIdentificationNumberPlaceholder(
                  country,
                  formValues.identificationType,
                ),
              )}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleIdentificationNumber(
                  event.target.value,
                  country,
                  formValues.identificationType,
                  setFieldValue,
                )
              }
            />
          </Box>
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap="2">
        <FormField.Input {...getInputAttributes('cardholderEmail')} />
        <Text fontSize="caption">{t('form.cardholderEmail.suggestion')}</Text>
      </Box>
      {recurrentPaymentCompatible && <Alert>{t('recurrencyAlert')}</Alert>}

      <Box display="flex" flexDirection="row" alignItems="center" gap="4">
        <Text fontSize="caption">{t('processedBy')}</Text>
      </Box>

      <input
        hidden
        id="input_expirationMonth"
        name="expirationMonth"
        value={formValues.expirationMonth}
      />
      <input
        hidden
        id="input_expirationYear"
        name="expirationYear"
        value={formValues.expirationYear}
      />
      <input
        hidden
        id="input_identificationNumber"
        name="identificationNumber"
        value={formValues.identificationNumber}
      />
      <select hidden id="select_issuer" name="issuer" />
      <select hidden id="select_installments" name="installments" />
    </Box>
  );
}

export default Fields;
